// src/pages/Estimating.tsx
// Estimating & Tender module — Drawings & Specs, Estimate Builder, Formal Tenders
// Design brief: Estimating_and_Tender.docx

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  MOCK_TENDERS, MOCK_CLIENT_DOCUMENTS, TENDER_STATUS_META, DOC_TYPE_META,
  BOQ_SECTION_META, TENDER_SECTIONS,
  Tender, ClientDocument, BOQItem, TenderStatus, BOQSection, DocumentType,
  TenderSectionCode, MarkupConfig, TenderSummary,
  getActiveTenders, countSectionsDone, getDaysUntilDeadline, calcTenderSummary,
} from '../utils/mockDataEstimating'
import { MOCK_SOPS } from '../utils/mockDataWorkInstructions'
import { TRADE_RATES, TRADE_RATE_MAP, TradeCode } from '../utils/mockDataClientSpecs'
import { MOCK_TASKS, MOCK_BOM } from '../utils/mockDataDrawings'
import { MOCK_JOBS } from '../utils/mockData'
import { MOCK_SCOPE_BRIEFS, BRIEF_STATUS_META } from '../utils/mockDataCommercial'
import {
  computeActualsVsEstimate, getHistoricalRates, suggestEstimateRate, updateHistoricalRates,
  type ActualsVsEstimate, type HistoricalRate,
} from '../utils/actualsLedger'
import {
  FileText, Upload, Plus, Search, ChevronRight, X, CheckCircle,
  AlertTriangle, Clock, DollarSign, Send, Download, Eye, Lock,
  FileSearch, BarChart3, Folder, Pencil, RefreshCw,
  ArrowRight, CheckSquare, Building, Calendar, TrendingUp,
  Package, Wrench, Shield, RotateCcw, ExternalLink, Info,
  ChevronDown, Copy, Zap, Briefcase, Sparkles,
} from 'lucide-react'
import { createJobFromTender, getAwardEventForTender, type AwardFormData } from '../utils/mockDataProjectGaps'

// ── Formatters ────────────────────────────────────────────────────
const fmtAUD = (n: number) =>
  new Intl.NumberFormat('en-AU',{style:'currency',currency:'AUD',minimumFractionDigits:0}).format(n)
const fmtDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString('en-AU',{day:'numeric',month:'short',year:'2-digit'}) : '—'
const fmtPct = (n: number) => `${n}%`

// ── Shared badge components ───────────────────────────────────────

function TenderStatusBadge({ status }: { status: TenderStatus }) {
  const m = TENDER_STATUS_META[status]
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${m.bg} ${m.text}`}>{m.label}</span>
}

function DocTypeBadge({ type }: { type: DocumentType }) {
  const m = DOC_TYPE_META[type]
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${m.bg} ${m.text}`}>
      {m.icon} {m.label}
    </span>
  )
}

function SectionProgress({ done, total }: { done: number; total: number }) {
  const pct = Math.round(done / total * 100)
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${pct === 100 ? 'bg-green-500' : 'bg-[#2E75B6]'}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] text-gray-400 tabular-nums">{done}/{total}</span>
    </div>
  )
}

// ── Tender Price Summary panel ────────────────────────────────────

function PriceSummaryPanel({ summary, markup }: { summary: TenderSummary; markup: MarkupConfig }) {
  return (
    <div className="bg-[#1F3864] rounded-xl p-4 text-white space-y-2">
      <div className="text-xs font-semibold text-blue-300 uppercase tracking-wide mb-3">Tender Price Summary</div>
      {[
        ['Direct labour (base)',          fmtAUD(summary.directLabour),        'text-white/60'],
        [`Labour + overhead (${markup.labourOverheadPct}%)`, fmtAUD(summary.labourWithOverhead), 'text-white'],
        [`Materials + margin (${markup.materialMarginPct}%)`, fmtAUD(summary.materialsWithMargin), 'text-white'],
        ['Subcontractors',                fmtAUD(summary.subcontractors),      'text-white'],
        ['Prelim & General',              fmtAUD(summary.prelimGeneral),       'text-white'],
        [`Contingency (${markup.contingencyPct}%)`, fmtAUD(summary.contingency), 'text-amber-300'],
        [`Profit margin (${markup.profitMarginPct}%)`, fmtAUD(summary.profitMargin), 'text-amber-300'],
      ].map(([label, value, cls]) => (
        <div key={String(label)} className="flex items-center justify-between text-xs">
          <span className="text-blue-200">{label}</span>
          <span className={`font-mono font-semibold ${cls}`}>{value}</span>
        </div>
      ))}
      <div className="border-t border-white/20 pt-2 mt-2">
        <div className="flex justify-between text-sm">
          <span className="text-blue-200">Tender price (ex GST)</span>
          <span className="font-bold text-white font-mono">{fmtAUD(summary.tenderExGST)}</span>
        </div>
        <div className="flex justify-between text-xs mt-1">
          <span className="text-blue-300">GST (10%)</span>
          <span className="text-blue-300 font-mono">{fmtAUD(summary.gst)}</span>
        </div>
      </div>
      <div className="bg-green-500/20 rounded-lg px-3 py-2 mt-2">
        <div className="flex justify-between items-center">
          <span className="text-xs text-green-300 font-semibold">TOTAL TENDER PRICE (inc GST)</span>
          <span className="text-xl font-bold text-green-300 font-mono">{fmtAUD(summary.tenderIncGST)}</span>
        </div>
      </div>
    </div>
  )
}

// ── Markup Controls ───────────────────────────────────────────────

function MarkupControls({ markup, onChange, canEdit }: {
  markup: MarkupConfig
  onChange: (key: keyof MarkupConfig, val: number) => void
  canEdit: boolean
}) {
  const fields: { key: keyof MarkupConfig; label: string }[] = [
    { key: 'labourOverheadPct',      label: 'Labour overhead & oncosts' },
    { key: 'materialMarginPct',      label: 'Materials margin' },
    { key: 'subcontractorMarginPct', label: 'Subcontractor margin' },
    { key: 'contingencyPct',         label: 'Contingency' },
    { key: 'profitMarginPct',        label: 'Profit margin' },
  ]
  return (
    <div className="space-y-2">
      {fields.map(({ key, label }) => (
        <div key={key} className="flex items-center gap-3">
          <span className="text-xs text-gray-600 w-44 flex-shrink-0">{label}</span>
          <input
            type="range" min={0} max={50} step={0.5}
            value={markup[key]}
            disabled={!canEdit}
            onChange={e => onChange(key, parseFloat(e.target.value))}
            className="flex-1 accent-[#2E75B6]"
          />
          <span className="text-xs font-bold text-[#1F3864] w-10 text-right">{markup[key]}%</span>
        </div>
      ))}
    </div>
  )
}

// ── BOQ Table ─────────────────────────────────────────────────────

function BOQTable({ items, canEdit }: { items: BOQItem[]; canEdit: boolean }) {
  const sections = [...new Set(items.map(i => i.sectionCode))] as BOQSection[]

  return (
    <div className="space-y-4">
      {sections.map(sec => {
        const secItems = items.filter(i => i.sectionCode === sec)
        const secTotal = secItems.reduce((s, i) => s + i.totalCharged, 0)
        const meta = BOQ_SECTION_META[sec]
        return (
          <div key={sec}>
            <div className={`flex items-center justify-between text-xs font-semibold uppercase tracking-wide mb-1 ${meta.color}`}>
              <span>{meta.label}</span>
              <span className="font-mono">{fmtAUD(secTotal)}</span>
            </div>
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Description</th>
                    <th className="text-right px-2 py-2 font-medium text-gray-600 w-16">Qty</th>
                    <th className="text-left px-2 py-2 font-medium text-gray-600 w-14">Unit</th>
                    <th className="text-right px-2 py-2 font-medium text-gray-600 w-20">Rate</th>
                    <th className="text-right px-2 py-2 font-medium text-gray-600 w-10">Mup</th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600 w-24">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {secItems.map(item => (
                    <tr key={item.id} className={`${item.isManualOverride ? 'bg-amber-50/40' : ''} hover:bg-gray-50`}>
                      <td className="px-3 py-2 text-gray-800">
                        {item.description}
                        {item.isManualOverride && (
                          <span className="ml-1 text-[10px] text-amber-600 font-medium">* manual</span>
                        )}
                        {item.sopRef && (
                          <span className="ml-1 text-[10px] text-blue-400">({item.sopRef})</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-gray-700">{item.quantity}</td>
                      <td className="px-2 py-2 text-gray-500">{item.unit}</td>
                      <td className="px-2 py-2 text-right font-mono text-gray-700">
                        {fmtAUD(item.unitRateCharged)}
                      </td>
                      <td className="px-2 py-2 text-right text-gray-400">{item.markupPct > 0 ? `${item.markupPct}%` : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-gray-900">
                        {fmtAUD(item.totalCharged)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      })}
      {canEdit && (
        <button className="w-full py-2 text-xs font-medium text-[#2E75B6] border border-dashed border-[#2E75B6]/40 rounded-lg hover:bg-blue-50 flex items-center justify-center gap-1.5">
          <Plus className="w-3.5 h-3.5" />Add BOQ line item
        </button>
      )}
    </div>
  )
}

// ── Tender editor panel ───────────────────────────────────────────

function TenderEditorPanel({ tender, onClose, canEdit, canSubmit, isPM }: {
  tender: Tender
  onClose: () => void
  canEdit: boolean
  canSubmit: boolean
  isPM: boolean
}) {
  const [activeSection, setActiveSection] = useState<TenderSectionCode>('A')
  const [markup, setMarkup] = useState<MarkupConfig>(tender.markupConfig)
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false)
  const [showAwardModal, setShowAwardModal] = useState(false)
  const [awardedJobResult, setAwardedJobResult] = useState<{ jobNumber: string; jobId: string } | null>(null)
  // Gap 7 — Drawings import state
  const [showDrawingsImport, setShowDrawingsImport] = useState(false)
  const [importJobId, setImportJobId] = useState(MOCK_JOBS[0]?.id ?? '')
  const [importedTaskIds, setImportedTaskIds] = useState<Set<string>>(new Set())
  const [boqItems, setBoqItems] = useState<BOQItem[]>(tender.boqItems)
  const [editingContent, setEditingContent] = useState<string | null>(null)

  // Gap 7 — Import from Drawings
  const handleDrawingsImport = () => {
    const jobTasks = MOCK_TASKS.filter(t => t.jobId === importJobId && t.level !== 1 && !importedTaskIds.has(t.id))
    const jobBom   = MOCK_BOM.filter(b => b.jobId === importJobId && b.complianceFlag !== 'NON_COMPLIANT')
    const newItems: BOQItem[] = []

    // Labour lines from tasks
    jobTasks.forEach(t => {
      const match = t.estimatedDuration.match(/(\d+(?:\.\d+)?)\s*(day|week|hour)/i)
      const num   = match ? parseFloat(match[1]) : 1
      const unit  = match?.[2]?.toLowerCase() ?? 'day'
      const hours = unit.startsWith('week') ? num * 40 : unit.startsWith('hour') ? num : num * 8
      const rate  = t.discipline === 'Electrical' || t.discipline === 'HV' ? 68.50
                  : t.discipline === 'Civil' ? 58.00
                  : t.discipline === 'Traffic Control' ? 42.00 : 55.00
      newItems.push({
        id: `boq-drw-labour-${t.id}-${Date.now()}`,
        sectionCode: 'LABOUR' as BOQSection,
        description: t.title, quantity: Math.round(hours), unit: 'hrs',
        unitRateBase: rate, unitRateCharged: rate, markupPct: 0, totalCharged: Math.round(hours) * rate,
        sopRef: t.specClauseRef ?? undefined,
        tradeCode: (t.discipline === 'Electrical' || t.discipline === 'HV' ? 'ELEC_A_GRADE'
                  : t.discipline === 'Civil' ? 'CIVIL_TRAD' : 'OTHER') as TradeCode,
        isManualOverride: false,
      } as unknown as BOQItem)
    })

    // Material lines from BOM
    const importedBomIds = new Set(boqItems.map(b => (b as any).bomLineId).filter(Boolean))
    jobBom.filter(b => !importedBomIds.has(b.id)).forEach(b => {
      newItems.push({
        id: `boq-drw-mat-${b.id}-${Date.now()}`,
        sectionCode: 'MATERIALS' as BOQSection,
        description: `${b.description}${b.specRef ? ` (${b.specRef})` : ''}`,
        quantity: b.quantity, unit: b.unit,
        unitRateBase: b.unitCostAud, unitRateCharged: b.unitCostAud,
        markupPct: 0, totalCharged: b.quantity * b.unitCostAud,
        isManualOverride: false, bomLineId: b.id,
      } as unknown as BOQItem)
    })

    setBoqItems(prev => [...prev, ...newItems])
    setImportedTaskIds(prev => new Set([...prev, ...jobTasks.map(t => t.id)]))
    setShowDrawingsImport(false)
  }

  const sectionData = tender.sections.find(s => s.code === activeSection)
  const completedSections = countSectionsDone(tender)
  const daysLeft = getDaysUntilDeadline(tender.submissionDeadline)
  const isUrgent = daysLeft !== null && daysLeft <= 3
  const liveSummary = calcTenderSummary(tender.boqItems, markup)

  const handleMarkupChange = (key: keyof MarkupConfig, val: number) => {
    setMarkup(prev => ({ ...prev, [key]: val }))
  }

  return (
    <div className="flex h-full">
      {/* Section nav */}
      <div className="w-36 flex-shrink-0 border-r bg-gray-50 flex flex-col">
        <div className="px-3 py-3 border-b">
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Sections</div>
          <SectionProgress done={completedSections} total={10} />
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {TENDER_SECTIONS.map(sec => {
            const secData = tender.sections.find(s => s.code === sec.code)
            const isActive = activeSection === sec.code
            return (
              <button key={sec.code} onClick={() => setActiveSection(sec.code as TenderSectionCode)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${isActive ? 'bg-[#1F3864] text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                  secData?.isComplete ? 'bg-green-500 text-white' : isActive ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {secData?.isComplete ? '✓' : sec.code}
                </span>
                <span className="text-[11px] leading-tight">{sec.title}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b bg-gray-50">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <span className="text-xs font-mono text-gray-400">{tender.reference}</span>
              <TenderStatusBadge status={tender.status} />
              {isUrgent && (
                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                  <Clock className="w-2.5 h-2.5" />{daysLeft}d left
                </span>
              )}
              {daysLeft !== null && !isUrgent && (
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />Due {fmtDate(tender.submissionDeadline)} ({daysLeft}d)
                </span>
              )}
            </div>
            <div className="text-sm font-semibold text-gray-900 truncate">{tender.projectName}</div>
            <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
              <Building className="w-3 h-3" />{tender.clientName} · RFT: {tender.rftReference}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Section content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {/* BOQ section */}
          {activeSection === 'E' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 text-sm">Section E — Bill of Quantities</h3>
                {canEdit && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => setShowDrawingsImport(!showDrawingsImport)}
                      className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5">
                      <Zap className="w-3 h-3" />Import from Drawings
                    </button>
                    <button className="text-xs text-[#2E75B6] hover:underline flex items-center gap-1">
                      <Upload className="w-3 h-3" />Import from WI cost
                    </button>
                  </div>
                )}
              </div>

              {/* Gap 7 — Drawings import panel */}
              {showDrawingsImport && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                        <Zap className="w-4 h-4 text-blue-600" />Import tasks &amp; BOM from Drawings
                      </p>
                      <p className="text-xs text-blue-700 mt-0.5">
                        Approved tasks → labour BOQ lines · Compliant BOM items → material BOQ lines · Non-compliant excluded · Duplicates skipped
                      </p>
                    </div>
                    <button onClick={() => setShowDrawingsImport(false)} className="p-1 text-gray-400 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Job to import from</label>
                    <select className="input text-sm w-full" value={importJobId} onChange={e => setImportJobId(e.target.value)}>
                      {MOCK_JOBS.map(j => {
                        const tasks = MOCK_TASKS.filter(t => t.jobId === j.id && t.level !== 1 && !importedTaskIds.has(t.id)).length
                        const bom   = MOCK_BOM.filter(b => b.jobId === j.id && b.complianceFlag !== 'NON_COMPLIANT').length
                        return <option key={j.id} value={j.id}>{(j as any).jobNumber} — {(j as any).description?.slice(0,35)} ({tasks} tasks, {bom} BOM items)</option>
                      })}
                    </select>
                  </div>
                  <div className="bg-white border border-blue-200 rounded-lg px-3 py-2 text-xs">
                    {(() => {
                      const tasks   = MOCK_TASKS.filter(t => t.jobId === importJobId && t.level !== 1 && !importedTaskIds.has(t.id))
                      const bom     = MOCK_BOM.filter(b => b.jobId === importJobId && b.complianceFlag !== 'NON_COMPLIANT')
                      const exclBom = MOCK_BOM.filter(b => b.jobId === importJobId && b.complianceFlag === 'NON_COMPLIANT')
                      return (
                        <div className="space-y-1 text-gray-700">
                          <div className="flex items-center gap-2"><span className="text-green-600 font-medium">✓ {tasks.length} tasks</span><span className="text-gray-400">→ labour BOQ lines</span></div>
                          <div className="flex items-center gap-2"><span className="text-green-600 font-medium">✓ {bom.length} BOM items</span><span className="text-gray-400">→ material BOQ lines</span></div>
                          {exclBom.length > 0 && <div className="text-red-500">✕ {exclBom.length} non-compliant items excluded</div>}
                          {importedTaskIds.size > 0 && <div className="text-gray-400">{importedTaskIds.size} task{importedTaskIds.size !== 1 ? 's' : ''} already imported — will be skipped</div>}
                        </div>
                      )
                    })()}
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowDrawingsImport(false)} className="btn-secondary text-sm">Cancel</button>
                    <button onClick={handleDrawingsImport} className="btn-primary text-sm flex items-center gap-2">
                      <Zap className="w-3.5 h-3.5" />Import into BOQ
                    </button>
                  </div>
                </div>
              )}

              <BOQTable items={boqItems} canEdit={canEdit} />
            </div>
          )}

          {/* Price summary section */}
          {activeSection === 'F' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 text-sm">Section F — Tender Price Summary</h3>
              <PriceSummaryPanel summary={liveSummary} markup={markup} />
              <div>
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Markup configuration</div>
                <MarkupControls markup={markup} onChange={handleMarkupChange} canEdit={canEdit} />
              </div>
            </div>
          )}

          {/* Text sections */}
          {activeSection !== 'E' && activeSection !== 'F' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 text-sm">
                  Section {activeSection} — {TENDER_SECTIONS.find(s => s.code === activeSection)?.title}
                </h3>
                {canEdit && sectionData && (
                  <button
                    onClick={() => setEditingContent(editingContent === activeSection ? null : activeSection)}
                    className="text-xs text-[#2E75B6] hover:underline flex items-center gap-1">
                    <Pencil className="w-3 h-3" />{editingContent === activeSection ? 'Done editing' : 'Edit'}
                  </button>
                )}
              </div>

              {sectionData?.isComplete === false && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  This section is not yet marked complete.
                  {canEdit && <button className="ml-auto text-amber-700 font-semibold hover:underline flex-shrink-0">Mark complete</button>}
                </div>
              )}

              {editingContent === activeSection ? (
                <textarea
                  className="input w-full text-sm resize-none leading-relaxed"
                  rows={14}
                  defaultValue={sectionData?.content ?? ''}
                  placeholder={`Enter content for Section ${activeSection}…`}
                />
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 min-h-48">
                  {sectionData?.content ? (
                    <pre className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap font-[Arial]">
                      {sectionData.content}
                    </pre>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                      <FileText className="w-8 h-8 mb-2 text-gray-300" />
                      <span className="text-sm">No content yet</span>
                      {canEdit && <button onClick={() => setEditingContent(activeSection)} className="mt-2 text-xs text-[#2E75B6] hover:underline">Click to add content</button>}
                    </div>
                  )}
                </div>
              )}

              {sectionData?.lastEditedById && (
                <div className="text-xs text-gray-400">
                  Last edited {fmtDate(sectionData.lastEditedAt)} by {sectionData.lastEditedById === 'u2' ? 'Jane Smith' : sectionData.lastEditedById === 'u4' ? 'Sarah Lee' : sectionData.lastEditedById}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action footer */}
        <div className="border-t bg-white px-4 py-3">
          {tender.status === 'AWARDED' && (
            <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              <CheckCircle className="w-3.5 h-3.5" />
              Awarded — {fmtAUD(tender.awardedAmount ?? 0)} · {fmtDate(tender.outcomeRecordedAt)}
            </div>
          )}

          {['DRAFT','IN_PREPARATION'].includes(tender.status) && !isPM && (
            <div className="flex gap-2">
              <button className="btn-secondary flex items-center gap-2 text-xs flex-1">
                <ArrowRight className="w-3.5 h-3.5" />Submit for PM review
              </button>
              <button onClick={() => generateTenderCoverLetterAndMethodStatement(tender)} className="btn-secondary flex items-center gap-2 text-xs">
                <Download className="w-3.5 h-3.5" />Export draft
              </button>
            </div>
          )}

          {['DRAFT','IN_PREPARATION','PENDING_PM_REVIEW'].includes(tender.status) && isPM && (
            <div className="flex gap-2 flex-wrap">
              {tender.status === 'PENDING_PM_REVIEW' && (
                <>
                  <button className="btn-primary flex items-center gap-2 text-xs">
                    <CheckCircle className="w-3.5 h-3.5" />Approve — mark ready to submit
                  </button>
                  <button className="btn-secondary flex items-center gap-2 text-xs text-amber-600">
                    <RotateCcw className="w-3.5 h-3.5" />Return for revision
                  </button>
                </>
              )}
              {['DRAFT','IN_PREPARATION'].includes(tender.status) && (
                <button className="btn-secondary flex items-center gap-2 text-xs">
                  <CheckCircle className="w-3.5 h-3.5" />Mark ready to submit
                </button>
              )}
            </div>
          )}

          {tender.status === 'READY_TO_SUBMIT' && (
            <div className="flex gap-2">
              {isPM ? (
                <>
                  {showConfirmSubmit ? (
                    <div className="flex-1 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-3">
                      <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                      <span className="text-xs text-red-800 flex-1">This will formally submit the tender to <strong>{tender.clientName}</strong>. This action cannot be undone.</span>
                      <button className="text-xs bg-red-600 text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-red-700 flex-shrink-0">
                        Confirm — submit now
                      </button>
                      <button onClick={() => setShowConfirmSubmit(false)} className="text-gray-400"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <>
                      <button onClick={() => setShowConfirmSubmit(true)}
                        className="flex-1 py-2 text-sm font-semibold bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2">
                        <Send className="w-4 h-4" />Submit tender to {tender.clientType === 'TMR' ? 'TMR' : tender.clientType === 'COUNCIL' ? 'council' : 'client'}
                      </button>
                      <button className="btn-secondary flex items-center gap-2 text-sm">
                        <Download className="w-4 h-4" />Export PDF
                      </button>
                      <button onClick={() => generateFullTenderDocument(tender)} className="btn-secondary flex items-center gap-2 text-sm">
                        <FileText className="w-4 h-4" />Full tender doc
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="flex-1 flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2 text-xs text-gray-500">
                  <Lock className="w-3.5 h-3.5 text-gray-400" />
                  Submission authorised by PM only. This tender is ready — awaiting PM to submit.
                </div>
              )}
            </div>
          )}

          {tender.status === 'SUBMITTED' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <div className="text-gray-500 flex items-center gap-1.5">
                  <Send className="w-3 h-3 text-purple-500" />
                  Submitted {fmtDate(tender.submittedAt)} by {tender.submittedByName}
                </div>
                {isPM && !awardedJobResult && (
                  <div className="flex gap-2">
                    <button onClick={() => setShowAwardModal(true)}
                      className="text-xs bg-green-100 text-green-700 hover:bg-green-200 px-2.5 py-1 rounded-lg font-medium flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />Record award &amp; activate job
                    </button>
                    <button className="text-xs bg-gray-100 text-gray-600 hover:bg-gray-200 px-2.5 py-1 rounded-lg font-medium">Record unsuccessful</button>
                  </div>
                )}
              </div>
              {awardedJobResult && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2.5 flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-green-800">Job {awardedJobResult.jobNumber} created &amp; activated</p>
                    <p className="text-xs text-green-700">BOQ imported as cost baseline · Scope imported as project brief · Tender linked to job record</p>
                  </div>
                  <button
                    onClick={() => document.dispatchEvent(new CustomEvent('tp-navigate', { detail: 'jobs' }))}
                    className="text-xs bg-green-600 text-white hover:bg-green-700 px-2.5 py-1 rounded-lg font-medium flex items-center gap-1">
                    <Briefcase className="w-3 h-3" />Open job →
                  </button>
                </div>
              )}
            </div>
          )}
          {showAwardModal && (
            <AwardTenderModal
              tender={tender}
              recordedByName={isPM ? 'Jane Smith' : 'PM'}
              onClose={() => setShowAwardModal(false)}
              onAwarded={(jobNumber, jobId) => {
                setAwardedJobResult({ jobNumber, jobId })
                setShowAwardModal(false)
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ── Drawings & Specs Tab ──────────────────────────────────────────

function DrawingsSpecsTab({ canUpload }: { canUpload: boolean }) {
  const [search, setSearch]   = useState('')
  const [typeFilter, setType] = useState<DocumentType | 'ALL'>('ALL')
  const [showUpload, setShowUpload] = useState(false)
  const [uploadStep, setUploadStep] = useState(1)
  const [selectedDocType, setSelectedDocType] = useState<DocumentType>('TENDER_DRAWING')
  const [tenderAction, setTenderAction] = useState<'start'|'add'|'none'>('start')

  const docs = MOCK_CLIENT_DOCUMENTS.filter(d => {
    if (typeFilter !== 'ALL' && d.type !== typeFilter) return false
    if (search && !d.title.toLowerCase().includes(search.toLowerCase()) &&
        !(d.drawingNumber ?? '').toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total documents', value: MOCK_CLIENT_DOCUMENTS.length, color: 'text-gray-900' },
          { label: 'Drawings', value: MOCK_CLIENT_DOCUMENTS.filter(d=>d.type==='TENDER_DRAWING').length, color: 'text-blue-700' },
          { label: 'Linked to tenders', value: MOCK_CLIENT_DOCUMENTS.filter(d=>d.linkedTenderIds.length>0).length, color: 'text-green-700' },
          { label: 'Unlinked docs', value: MOCK_CLIENT_DOCUMENTS.filter(d=>d.linkedTenderIds.length===0).length, color: 'text-amber-600' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-xs text-gray-500">{label}</div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input className="input pl-9 text-sm" placeholder="Search drawings, specs…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input text-sm w-auto" value={typeFilter} onChange={e => setType(e.target.value as any)}>
          <option value="ALL">All types</option>
          {(Object.keys(DOC_TYPE_META) as DocumentType[]).map(t => (
            <option key={t} value={t}>{DOC_TYPE_META[t].label}</option>
          ))}
        </select>
        {canUpload && (
          <button onClick={() => { setShowUpload(true); setUploadStep(1) }} className="btn-primary flex items-center gap-2 text-sm ml-auto">
            <Upload className="w-4 h-4" />Upload document
          </button>
        )}
      </div>

      {/* Upload / import dialog */}
      {showUpload && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-blue-900">Upload client document</h4>
              <div className="flex items-center gap-2 mt-1">
                {[1,2,3].map(step => (
                  <span key={step} className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${uploadStep >= step ? 'bg-[#1F3864] text-white' : 'bg-gray-200 text-gray-400'}`}>{step}</span>
                ))}
                <span className="text-xs text-gray-500">
                  {uploadStep === 1 ? 'Upload file' : uploadStep === 2 ? 'Classify document' : 'Tender action'}
                </span>
              </div>
            </div>
            <button onClick={() => setShowUpload(false)}><X className="w-4 h-4 text-gray-400" /></button>
          </div>

          {uploadStep === 1 && (
            <div className="space-y-3">
              <div className="border-2 border-dashed border-blue-300 bg-white rounded-xl p-8 text-center">
                <Upload className="w-8 h-8 text-blue-300 mx-auto mb-2" />
                <div className="text-sm text-gray-600 font-medium">Drop files here or browse</div>
                <div className="text-xs text-gray-400 mt-1">PDF, DWG (auto-converted), DOCX, XLSX · Max 50 MB</div>
                <button className="mt-3 btn-secondary text-xs">Browse files</button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Job reference</label>
                  <select className="input text-sm"><option>TP-2026-001 — Sandgate Rd</option><option>TP-2026-003 — Maroochydore Rd</option><option>TP-2026-004 — Virginia ITS</option></select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Date received</label>
                  <input className="input text-sm" type="date" defaultValue={new Date().toISOString().split('T')[0]} />
                </div>
              </div>
              <div className="flex justify-end">
                <button onClick={() => setUploadStep(2)} className="btn-primary text-sm">Next — classify →</button>
              </div>
            </div>
          )}

          {uploadStep === 2 && (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-700">
                ✓ File uploaded: <strong>SCC-J3-Drawing-Register-Rev2.pdf</strong> (8.1 MB)
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Document type</label>
                <div className="grid grid-cols-3 gap-2">
                  {(Object.keys(DOC_TYPE_META) as DocumentType[]).map(t => (
                    <button key={t} onClick={() => setSelectedDocType(t)}
                      className={`text-left p-2.5 rounded-lg border text-xs transition-colors ${selectedDocType === t ? 'border-[#2E75B6] bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                      <div className="text-base mb-1">{DOC_TYPE_META[t].icon}</div>
                      <div className="font-medium text-gray-800">{DOC_TYPE_META[t].label}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Drawing number (if applicable)</label>
                  <input className="input text-sm" placeholder="e.g. J3-DWG-002" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Revision</label>
                  <input className="input text-sm" placeholder="e.g. Rev B" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                  <input className="input text-sm" placeholder="Drawing or document title" />
                </div>
              </div>
              <div className="flex justify-between">
                <button onClick={() => setUploadStep(1)} className="btn-secondary text-sm">← Back</button>
                <button onClick={() => setUploadStep(3)} className="btn-primary text-sm">Next — tender action →</button>
              </div>
            </div>
          )}

          {uploadStep === 3 && (
            <div className="space-y-3">
              <div className="bg-blue-100/60 rounded-lg px-3 py-2 text-xs text-blue-800">
                <Info className="w-3 h-3 inline mr-1" />
                Would you like to use this document as a source for a tender estimate?
              </div>
              <div className="space-y-2">
                {[
                  { value: 'start', label: 'Start new tender from this document', desc: 'Creates a new DRAFT tender for this job, pre-populated with this document\'s details' },
                  { value: 'add', label: 'Add to an existing tender', desc: 'Attach this document to an existing open tender as a source document' },
                  { value: 'none', label: 'Store only — no tender action', desc: 'Save to the document library without starting or updating a tender' },
                ].map(opt => (
                  <label key={opt.value} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${tenderAction === opt.value ? 'border-[#2E75B6] bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
                    <input type="radio" name="tenderAction" value={opt.value} checked={tenderAction === opt.value}
                      onChange={() => setTenderAction(opt.value as any)} className="mt-0.5 accent-[#2E75B6]" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{opt.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
              {tenderAction === 'add' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Select tender</label>
                  <select className="input text-sm">
                    {getActiveTenders().map(t => <option key={t.id}>{t.reference} — {t.projectName}</option>)}
                  </select>
                </div>
              )}
              <div className="flex justify-between">
                <button onClick={() => setUploadStep(2)} className="btn-secondary text-sm">← Back</button>
                <button onClick={() => setShowUpload(false)} className="btn-primary text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {tenderAction === 'start' ? 'Upload & start tender' : tenderAction === 'add' ? 'Upload & add to tender' : 'Upload & store only'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Document list */}
      <div className="space-y-2">
        {docs.map(doc => (
          <div key={doc.id} className={`bg-white border rounded-xl p-4 hover:shadow-sm transition-all ${doc.linkedTenderIds.length === 0 ? 'border-amber-200' : 'border-gray-200 hover:border-gray-300'}`}>
            <div className="flex items-start gap-3">
              <div className="text-2xl flex-shrink-0">{DOC_TYPE_META[doc.type].icon}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      {doc.drawingNumber && <span className="text-xs font-mono text-gray-500">{doc.drawingNumber}{doc.revisionCode ? ` Rev ${doc.revisionCode}` : ''}</span>}
                      <DocTypeBadge type={doc.type} />
                      {doc.linkedTenderIds.length === 0 && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Not linked to tender</span>
                      )}
                    </div>
                    <div className="font-medium text-gray-900 text-sm">{doc.title}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-1.5 flex-wrap text-xs text-gray-400">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Received {fmtDate(doc.receivedDate)}</span>
                  <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{doc.fileSize}</span>
                  <span className="flex items-center gap-1"><Package className="w-3 h-3" />{doc.uploadedByName}</span>
                  {doc.linkedTenderIds.length > 0 && (
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle className="w-3 h-3" />{doc.linkedTenderIds.length} tender{doc.linkedTenderIds.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {doc.notes && <div className="text-xs text-gray-500 mt-1 italic">{doc.notes}</div>}
              </div>
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 py-1 px-2 rounded-lg hover:bg-gray-100">
                  <Eye className="w-3 h-3" />View
                </button>
                {doc.linkedTenderIds.length === 0 && canUpload && (
                  <button className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 py-1 px-2 rounded-lg hover:bg-blue-50 font-medium">
                    <Plus className="w-3 h-3" />Start tender
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Estimate Builder Tab ──────────────────────────────────────────

function EstimateBuilderTab({ canEdit }: { canEdit: boolean }) {
  const [selectedSops, setSelectedSops] = useState<Record<string, number>>({})
  const [markup, setMarkup] = useState<MarkupConfig>({ labourOverheadPct:20, materialMarginPct:15, subcontractorMarginPct:10, contingencyPct:5, profitMarginPct:8 })
  const [showSopPicker, setShowSopPicker] = useState(false)
  const [showHistorical, setShowHistorical] = useState(false)
  const historicalRates = getHistoricalRates()

  // Calculate labour estimate from selected SOPs
  const labourLines = Object.entries(selectedSops).map(([sopId, qty]) => {
    const sop = MOCK_SOPS.find(s => s.id === sopId)
    if (!sop) return null
    const totalHrs = (sop.steps as any[]).reduce((s: number, st: any) => s + (st.estimatedHours ?? 0), 0)
    // Phase 4: check historical rate for this SOP
    const hist = suggestEstimateRate(sop.id)
    const unitCost = hist.suggested ?? (totalHrs * 95)
    const q = qty as number
    return { sopId, sop, qty: q, unitCost, total: unitCost * q, hist }
  }).filter(Boolean) as { sopId: string; sop: any; qty: number; unitCost: number; total: number; hist: ReturnType<typeof suggestEstimateRate> }[]

  const directLabour = labourLines.reduce((s, l) => s + l.total, 0)
  const labourOH = directLabour * (1 + markup.labourOverheadPct / 100)
  const contingency = labourOH * (markup.contingencyPct / 100)
  const margin = (labourOH + contingency) * (markup.profitMarginPct / 100)
  const exGST = labourOH + contingency + margin

  return (
    <div className="space-y-4">
      {/* Phase 4: Historical rate banner */}
      {historicalRates.length > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-indigo-800">
            <TrendingUp className="w-3.5 h-3.5 text-indigo-600" />
            <span><strong>Historical rate library active</strong> — {historicalRates.length} SOP rates from {Math.max(...historicalRates.map(h => h.sampleCount))} jobs. Estimates are auto-updated with real actuals.</span>
          </div>
          <button onClick={() => setShowHistorical(!showHistorical)} className="text-xs text-indigo-600 hover:underline flex-shrink-0">
            {showHistorical ? 'Hide' : 'View rates'}
          </button>
        </div>
      )}

      {/* Historical rate table */}
      {showHistorical && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b text-xs font-semibold text-gray-700">Historical rate library — from actual job performance</div>
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left py-2 px-3 font-medium text-gray-600">SOP</th>
                <th className="text-left py-2 px-3 font-medium text-gray-600">Description</th>
                <th className="text-right py-2 px-3 font-medium text-gray-600">Avg actual $/unit</th>
                <th className="text-right py-2 px-3 font-medium text-gray-600">Range</th>
                <th className="text-right py-2 px-3 font-medium text-gray-600">Jobs</th>
                <th className="text-left py-2 px-3 font-medium text-gray-600">Confidence</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {historicalRates.map(h => {
                const conf = h.sampleCount >= 4 ? { label: 'High', cls: 'text-green-700 bg-green-50' } : h.sampleCount >= 2 ? { label: 'Medium', cls: 'text-amber-700 bg-amber-50' } : { label: 'Low', cls: 'text-gray-500 bg-gray-50' }
                return (
                  <tr key={h.sopRef} className="hover:bg-gray-50">
                    <td className="py-2 px-3 font-mono text-gray-500">{h.sopRef}</td>
                    <td className="py-2 px-3 text-gray-700">{h.description}</td>
                    <td className="py-2 px-3 text-right font-medium">{fmtAUD(h.avgActualRate)}</td>
                    <td className="py-2 px-3 text-right text-gray-400">{fmtAUD(h.minActualRate)} – {fmtAUD(h.maxActualRate)}</td>
                    <td className="py-2 px-3 text-right">{h.sampleCount}</td>
                    <td className="py-2 px-3"><span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${conf.cls}`}>{conf.label}</span></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
        <Info className="w-4 h-4 inline mr-1.5" />
        Select approved SOPs to auto-populate labour costs from Work Instruction hour estimates. Where historical actuals are available, the historical average is used instead of the theoretical SOP rate.
      </div>

      {/* WI cost picker */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-gray-900">Labour cost from Work Instructions</div>
          {canEdit && (
            <button onClick={() => setShowSopPicker(!showSopPicker)} className="btn-secondary text-xs flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" />Add SOP
            </button>
          )}
        </div>

        {showSopPicker && (
          <div className="bg-white border border-gray-200 rounded-xl p-3 mb-3 space-y-2 max-h-48 overflow-y-auto">
            {MOCK_SOPS.filter(s => s.status === 'APPROVED').map(sop => {
              const hrs = (sop.steps as any[]).reduce((s: number, st: any) => s + (st.estimatedHours ?? 0), 0)
              return (
                <label key={sop.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                  <input type="checkbox" checked={!!selectedSops[sop.id]}
                    onChange={e => setSelectedSops(prev => {
                      const n = { ...prev }
                      if (e.target.checked) n[sop.id] = 1; else delete n[sop.id]
                      return n
                    })} className="accent-[#2E75B6]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono text-gray-400">{sop.reference}</div>
                    <div className="text-sm font-medium text-gray-800 truncate">{sop.title}</div>
                  </div>
                  {hrs > 0 && <span className="text-xs text-blue-600 font-medium flex-shrink-0">{hrs}h est.</span>}
                </label>
              )
            })}
          </div>
        )}

        {labourLines.length > 0 ? (
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="bg-gray-50 border-b">
                <th className="text-left px-3 py-2 font-medium text-gray-600">SOP / Work task</th>
                <th className="text-right px-2 py-2 font-medium text-gray-600 w-16">Qty</th>
                <th className="text-right px-2 py-2 font-medium text-gray-600 w-20">Unit cost</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600 w-24">Total</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-50">
                {labourLines.map(line => (
                  <tr key={line.sopId} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="text-xs font-mono text-gray-400">{line.sop.reference}</div>
                      <div className="text-gray-800">{line.sop.title}</div>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <input type="number" min={1} max={99} value={line.qty}
                        onChange={e => setSelectedSops(prev => ({ ...prev, [line.sopId]: parseInt(e.target.value)||1 }))}
                        className="w-12 text-right border border-gray-200 rounded px-1 py-0.5 text-xs" />
                    </td>
                    <td className="px-2 py-2 text-right font-mono">{fmtAUD(line.unitCost)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{fmtAUD(line.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="border border-dashed border-gray-300 rounded-xl py-10 text-center text-gray-400 text-sm">
            Select SOPs above to auto-populate labour costs
          </div>
        )}
      </div>

      {/* Markup controls */}
      <div>
        <div className="text-sm font-semibold text-gray-900 mb-2">Markup configuration</div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <MarkupControls markup={markup} onChange={(k, v) => setMarkup(prev => ({ ...prev, [k]: v }))} canEdit={canEdit} />
        </div>
      </div>

      {/* Live estimate */}
      {directLabour > 0 && (
        <div className="space-y-3">
          <div className="text-sm font-semibold text-gray-900">Live estimate</div>
          <PriceSummaryPanel summary={{
            directLabour: Math.round(directLabour),
            labourWithOverhead: Math.round(labourOH),
            directMaterials: 0, materialsWithMargin: 0, subcontractors: 0,
            subtotalDirect: Math.round(labourOH),
            prelimGeneral: 0,
            contingency: Math.round(contingency),
            profitMargin: Math.round(margin),
            tenderExGST: Math.round(exGST),
            gst: Math.round(exGST * 0.1),
            tenderIncGST: Math.round(exGST * 1.1),
          }} markup={markup} />
          {canEdit && (
            <button className="w-full btn-primary flex items-center justify-center gap-2 text-sm">
              <ArrowRight className="w-4 h-4" />Save to tender BOQ
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── Tenders Tab ───────────────────────────────────────────────────

function TendersTab({ isPM, isPC }: { isPM: boolean; isPC: boolean }) {
  const [statusFilter, setStatusFilter] = useState<TenderStatus | 'ALL' | 'ACTIVE'>('ACTIVE')
  const [selected, setSelected] = useState<Tender | null>(null)

  const filtered = MOCK_TENDERS.filter(t => {
    if (statusFilter === 'ACTIVE') return !['AWARDED','UNSUCCESSFUL','WITHDRAWN'].includes(t.status)
    if (statusFilter !== 'ALL') return t.status === statusFilter
    return true
  })

  const actionCount = MOCK_TENDERS.filter(t =>
    (t.status === 'PENDING_PM_REVIEW' && isPM) ||
    (['DRAFT','IN_PREPARATION'].includes(t.status) && !isPM && isPC)
  ).length

  return (
    <div className="space-y-4">
      {actionCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <strong>{actionCount} tender{actionCount !== 1 ? 's' : ''}</strong> require{actionCount === 1 ? 's' : ''} action.
        </div>
      )}

      <div className="flex gap-2 flex-wrap items-center">
        {(['ACTIVE','ALL','DRAFT','IN_PREPARATION','PENDING_PM_REVIEW','READY_TO_SUBMIT','SUBMITTED','AWARDED'] as (TenderStatus|'ALL'|'ACTIVE')[]).map(f => {
          const count = f === 'ACTIVE' ? getActiveTenders().length
            : f === 'ALL' ? MOCK_TENDERS.length
            : MOCK_TENDERS.filter(t => t.status === f).length
          if (count === 0 && f !== 'ACTIVE' && f !== 'ALL') return null
          return (
            <button key={f} onClick={() => setStatusFilter(f)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-1.5 ${statusFilter === f ? 'bg-[#1F3864] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {f === 'ACTIVE' ? 'Active' : f === 'ALL' ? 'All tenders' : TENDER_STATUS_META[f as TenderStatus].label}
              <span className={`text-[10px] font-bold rounded-full px-1.5 ${statusFilter === f ? 'bg-white/20' : 'bg-white text-gray-500'}`}>{count}</span>
            </button>
          )
        })}
        {isPM && (
          <button className="btn-primary flex items-center gap-2 text-sm ml-auto">
            <Plus className="w-4 h-4" />New tender
          </button>
        )}
      </div>

      <div className="flex gap-4 items-start">
        <div className="flex-1 min-w-0 space-y-2">
          {filtered.length === 0 ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl flex flex-col items-center py-16">
              <FileText className="w-10 h-10 text-gray-300 mb-3" />
              <div className="font-medium text-gray-500">No tenders in this category</div>
            </div>
          ) : filtered.map(t => {
            const isSelected = selected?.id === t.id
            const done = countSectionsDone(t)
            const daysLeft = getDaysUntilDeadline(t.submissionDeadline)
            const isUrgent = daysLeft !== null && daysLeft <= 3
            const needsAction = (t.status === 'PENDING_PM_REVIEW' && isPM)
            return (
              <div key={t.id} onClick={() => setSelected(isSelected ? null : t)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${
                  isSelected ? 'border-blue-400 ring-2 ring-blue-100 shadow-sm' :
                  needsAction ? 'border-amber-300' :
                  isUrgent ? 'border-red-200' : 'border-gray-200 hover:border-gray-300'
                }`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-gray-400">{t.reference}</span>
                      <TenderStatusBadge status={t.status} />
                      {needsAction && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">⚑ Review needed</span>}
                      {isUrgent && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{daysLeft}d left</span>}
                    </div>
                    <div className="font-semibold text-gray-900 text-sm">{t.projectName}</div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                      <Building className="w-3 h-3" />{t.clientName} · {t.rftReference}
                    </div>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <span className="text-sm font-bold text-gray-900">{fmtAUD(t.summary.tenderIncGST)}</span>
                      <span className="text-xs text-gray-400">inc GST</span>
                      {t.submissionDeadline && (
                        <span className={`text-xs flex items-center gap-1 ${isUrgent ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                          <Calendar className="w-3 h-3" />Due {fmtDate(t.submissionDeadline)}
                        </span>
                      )}
                    </div>
                    {['DRAFT','IN_PREPARATION','PENDING_PM_REVIEW'].includes(t.status) && (
                      <div className="mt-2"><SectionProgress done={done} total={10} /></div>
                    )}
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 ${isSelected ? 'text-blue-500' : 'text-gray-400'}`} />
                </div>
              </div>
            )
          })}
        </div>

        {selected && (
          <div className="w-[560px] flex-shrink-0 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm sticky top-6"
            style={{ height: 'calc(100vh - 160px)' }}>
            <TenderEditorPanel
              tender={selected}
              onClose={() => setSelected(null)}
              canEdit={isPM || (isPC && !['SUBMITTED','AWARDED','UNSUCCESSFUL','WITHDRAWN'].includes(selected.status))}
              canSubmit={isPM}
              isPM={isPM}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// ── Award Tender Modal (PM-01 + PM-02) ──────────────────────────

function AwardTenderModal({
  tender,
  onClose,
  onAwarded,
  recordedByName,
}: {
  tender: Tender
  onClose: () => void
  onAwarded: (jobNumber: string, jobId: string) => void
  recordedByName: string
}) {
  const [form, setForm] = useState<AwardFormData>({
    awardedAmount: tender.summary.tenderExGST,
    contractNumber: '',
    contractStartDate: new Date().toISOString().slice(0, 10),
    targetEndDate: '',
    siteAddress: '',
    recordedByName,
  })
  const set = (k: keyof AwardFormData, v: string | number) =>
    setForm(p => ({ ...p, [k]: v }))
  const [step, setStep] = useState<1 | 2>(1)
  const [importing, setImporting] = useState(false)

  const handleCreate = () => {
    setImporting(true)
    setTimeout(() => {
      const { job, event } = createJobFromTender(tender, form)
      setImporting(false)
      onAwarded(event.jobNumber, job.id)
    }, 1200)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-green-50">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />Record award &amp; activate project
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{tender.reference} · {tender.projectName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex border-b">
          {[{ n: 1, label: 'Award details' }, { n: 2, label: 'Job setup' }].map(s => (
            <button key={s.n} onClick={() => step > 1 && setStep(s.n as 1 | 2)}
              className={`flex-1 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                step === s.n ? 'border-green-600 text-green-700' : 'border-transparent text-gray-400'
              }`}>
              <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center mr-1.5 text-[10px] font-bold ${
                step === s.n ? 'bg-green-600 text-white' : step > s.n ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
              }`}>{step > s.n ? '✓' : s.n}</span>
              {s.label}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-4">
          {step === 1 && (
            <>
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-xs text-green-800">
                <Sparkles className="w-3.5 h-3.5 inline mr-1 text-green-600" />
                On award, the BOQ ({tender.boqItems.length} lines, {fmtAUD(tender.summary.tenderExGST)} ex GST) will be
                imported as the project cost baseline. Scope from Section C becomes the project brief.
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Awarded amount (ex GST) <span className="text-red-500">*</span></label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <input type="number" className="input pl-7"
                    value={form.awardedAmount}
                    onChange={e => set('awardedAmount', parseFloat(e.target.value) || 0)} />
                </div>
                {form.awardedAmount !== tender.summary.tenderExGST && (
                  <p className="text-xs text-amber-600 mt-1">
                    Differs from tender price {fmtAUD(tender.summary.tenderExGST)} by {fmtAUD(Math.abs(form.awardedAmount - tender.summary.tenderExGST))}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Client contract / PO number</label>
                <input className="input" placeholder="e.g. TMR-C-2026-1142" value={form.contractNumber}
                  onChange={e => set('contractNumber', e.target.value)} />
              </div>
              <div className="flex justify-end">
                <button onClick={() => setStep(2)} disabled={!form.awardedAmount}
                  className="btn-primary text-sm disabled:opacity-50 flex items-center gap-2">
                  Next — job setup <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Contract start date <span className="text-red-500">*</span></label>
                  <input type="date" className="input" value={form.contractStartDate}
                    onChange={e => set('contractStartDate', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Target completion</label>
                  <input type="date" className="input" value={form.targetEndDate}
                    onChange={e => set('targetEndDate', e.target.value)} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Site address</label>
                <input className="input" placeholder="Street address, suburb QLD postcode"
                  value={form.siteAddress} onChange={e => set('siteAddress', e.target.value)} />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs">
                <p className="font-semibold text-blue-900 mb-1">What gets imported automatically:</p>
                <ul className="space-y-0.5 text-blue-800">
                  <li>✓ {tender.boqItems.length} BOQ lines → job cost baseline</li>
                  <li>✓ Section C scope → project brief</li>
                  <li>✓ Client: {tender.clientName}</li>
                  <li>✓ Tender reference {tender.reference} → linked to job record</li>
                </ul>
              </div>
              <div className="flex justify-between gap-2">
                <button onClick={() => setStep(1)} className="btn-secondary text-sm">← Back</button>
                <button onClick={handleCreate} disabled={importing || !form.contractStartDate}
                  className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50">
                  {importing
                    ? <><RefreshCw className="w-4 h-4 animate-spin" />Importing BOQ…</>
                    : <><Briefcase className="w-4 h-4" />Create &amp; activate job</>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────




// ─── GST formatting helpers ────────────────────────────────────────
// fmtX: plain $ amount (no AUD prefix — assumed AUD for all documents)
// gstRow: returns three table rows for ex-GST / GST / total presentation
function fmtX(n: number): string {
  return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}
function gstRows(exGST: number, label = 'Amount'): string {
  const gst   = exGST * 0.1
  const total = exGST + gst
  return `
    <tr style="border-top:1px solid #e5e7eb">
      <td colspan="999" style="padding:2px"></td>
    </tr>
    <tr>
      <td colspan="2" style="padding:5px 10px;text-align:right;font-size:9.5pt;color:#555">${label} (ex GST)</td>
      <td style="padding:5px 10px;text-align:right;font-family:monospace">${fmtX(exGST)}</td>
    </tr>
    <tr>
      <td colspan="2" style="padding:5px 10px;text-align:right;font-size:9.5pt;color:#555">GST (10%)</td>
      <td style="padding:5px 10px;text-align:right;font-family:monospace">${fmtX(gst)}</td>
    </tr>
    <tr style="background:#f0f9ff">
      <td colspan="2" style="padding:6px 10px;text-align:right;font-weight:bold">Total (inc GST)</td>
      <td style="padding:6px 10px;text-align:right;font-weight:bold;font-family:monospace">${fmtX(total)}</td>
    </tr>`
}

// ─────────────────────────────────────────────────────────────────
// PHASE 7 — TENDER COVER LETTER + METHOD STATEMENT
// Pulls Section A (cover sheet) and Section D (method statement)
// from the tender and formats them as a client-ready submission
// document with MCB letterhead.
// ─────────────────────────────────────────────────────────────────
function generateTenderCoverLetterAndMethodStatement(tender: Tender) {
  const now = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const coverSection  = tender.sections.find(s => s.code === 'A')
  const methodSection = tender.sections.find(s => s.code === 'D')
  const execSection   = tender.sections.find(s => s.code === 'B')

  const renderContent = (text: string | undefined) =>
    (text ?? 'Not yet completed.').split('\n').map(line => `<p style="margin:0 0 8px">${line || '&nbsp;'}</p>`).join('')

  const incompleteWarning = (sec: typeof coverSection, label: string) =>
    sec && !sec.isComplete ? `<div class="warn-box">⚠ ${label} is marked incomplete in the tender editor — review before submission.</div>` : ''

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Tender Cover Letter &amp; Method Statement — ${tender.reference}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11pt;color:#111;margin:20mm}
h1{font-size:22pt;font-weight:900;color:#1B3A6B;margin:0}
h2{font-size:13pt;color:#1B3A6B;border-bottom:2px solid #1B3A6B;padding-bottom:5px;margin:24px 0 12px;page-break-after:avoid}
.hdr{border-bottom:3px solid #1B3A6B;padding-bottom:12px;margin-bottom:18px;display:flex;justify-content:space-between;align-items:flex-end}
.ref-box{background:#e8f0fe;border-left:4px solid #1B3A6B;padding:10px 16px;margin:14px 0;font-size:10pt}
.section{page-break-before:always}
.section:first-of-type{page-break-before:avoid}
.warn-box{background:#fef3c7;border:1px solid #fbbf24;border-radius:4px;padding:8px 14px;margin-bottom:10px;font-size:9.5pt;color:#92400e}
p{line-height:1.7}
.sig-block{margin-top:30px}
.sig-line{border-bottom:1.5px solid #333;height:32px;margin-bottom:4px}
.sig-lbl{font-size:8pt;color:#888}
.footer{font-size:7.5pt;color:#aaa;text-align:center;margin-top:18px;border-top:1px solid #eee;padding-top:7px}
@media print{body{margin:15mm}}
</style></head><body>

<div class="hdr">
  <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#888">ABN 44 128 993 087 · 14 Commerce Dr, Salisbury QLD 4107 · 07 3344 5500 · tenders@mcbce.com.au</div></div>
  <div style="text-align:right"><div style="font-size:9pt;color:#888">Date: ${now}</div><div style="font-size:9pt;color:#888">Ref: ${tender.reference}</div></div>
</div>

<div class="ref-box">
  <strong>RFT reference:</strong> ${tender.rftReference} &nbsp;|&nbsp; <strong>Client:</strong> ${tender.clientName} &nbsp;|&nbsp; <strong>Project:</strong> ${tender.projectName}
</div>

<div class="section">
<h2>Cover Letter</h2>
${incompleteWarning(coverSection, 'Cover sheet')}
${renderContent(coverSection?.content)}
${execSection?.content && execSection.content.length > 10 ? `<h2 style="border:none;font-size:11pt;margin-top:18px">Executive Summary</h2>${renderContent(execSection.content)}` : ''}
<div class="sig-block">
  <p>Yours faithfully,</p>
  <div class="sig-line"></div>
  <div class="sig-lbl">Authorised signatory — MCB Civil Electrical Pty Ltd · name, title &amp; date</div>
</div>
</div>

<div class="section">
<h2>Method Statement</h2>
${incompleteWarning(methodSection, 'Method statement')}
${renderContent(methodSection?.content)}
</div>

<div class="footer">MCB Civil Electrical Pty Ltd · ${tender.reference} · ${tender.rftReference} · Cover Letter &amp; Method Statement · Generated ${now}<br>All prices are exclusive of GST (10%) unless stated otherwise. GST will be itemised on all tax invoices as required under A New Tax System (Goods and Services Tax) Act 1999 (Cth).<br>This document forms part of the formal tender submission and should be read in conjunction with the full tender package (Sections A–J).</div>
</body></html>`
  const w = window.open('', '_blank', 'width=1000,height=820')
  if (!w) { alert('Pop-up blocked — allow pop-ups to print document'); return }
  w.document.write(html); w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}
// ── CM-06: Generate full tender document (Sections A–J) ────────────────
function generateFullTenderDocument(tender: Tender) {
  const now = new Date().toLocaleDateString('en-AU', { day:'numeric', month:'long', year:'numeric' })
  const secHtml = tender.sections.map(sec => {
    const title = TENDER_SECTIONS.find(s=>s.code===sec.code)?.title ?? sec.code
    const content = sec.content
      ? sec.content.split('\n').map(l=>`<p>${l||'&nbsp;'}</p>`).join('')
      : '<p style="color:#9ca3af;font-style:italic">Content not yet entered.</p>'
    const badge = sec.isComplete
      ? '<span style="background:#dcfce7;color:#166534;font-size:8pt;padding:1px 8px;border-radius:10px;font-weight:bold">COMPLETE</span>'
      : '<span style="background:#fef3c7;color:#92400e;font-size:8pt;padding:1px 8px;border-radius:10px;font-weight:bold">INCOMPLETE</span>'
    return `<div style="page-break-before:always;padding-top:8px">
      <h2 style="color:#1F3864;border-bottom:2px solid #1F3864;padding-bottom:6px;display:flex;align-items:center;gap:10px">Section ${sec.code} — ${title} ${badge}</h2>
      ${content}</div>`
  }).join('')

  // BOQ table
  const boqBySection = (() => {
    const groups: Record<string,typeof tender.boqItems> = {}
    tender.boqItems.forEach(item => {
      if (!groups[item.sectionCode]) groups[item.sectionCode] = []
      groups[item.sectionCode].push(item)
    })
    return groups
  })()
  const boqHtml = `<div style="page-break-before:always">
    <h2 style="color:#1F3864;border-bottom:2px solid #1F3864;padding-bottom:6px">Section E — Bill of Quantities</h2>
    ${Object.entries(boqBySection).map(([sec, items]) => {
      const total = items.reduce((s,i)=>s+i.totalCharged,0)
      return `<h3 style="color:#374151;font-size:11pt;margin:16px 0 6px">${sec} — $${total.toLocaleString('en-AU',{minimumFractionDigits:0})}</h3>
        <table style="width:100%;border-collapse:collapse;font-size:9.5pt;margin-bottom:10px">
          <thead><tr style="background:#1F3864;color:white">
            <th style="padding:5px 8px;text-align:left">Description</th>
            <th style="padding:5px 8px;text-align:right;width:60px">Qty</th>
            <th style="padding:5px 8px;text-align:left;width:50px">Unit</th>
            <th style="padding:5px 8px;text-align:right;width:90px">Rate</th>
            <th style="padding:5px 8px;text-align:right;width:90px">Total</th>
          </tr></thead>
          <tbody>${items.map((item,i)=>`<tr style="background:${i%2===0?'#f9fafb':'white'};border-bottom:1px solid #e5e7eb">
            <td style="padding:4px 8px">${item.description}</td>
            <td style="padding:4px 8px;text-align:right">${item.quantity}</td>
            <td style="padding:4px 8px">${item.unit}</td>
            <td style="padding:4px 8px;text-align:right;font-family:monospace">$${item.unitRateCharged.toFixed(2)}</td>
            <td style="padding:4px 8px;text-align:right;font-family:monospace;font-weight:bold">$${item.totalCharged.toLocaleString('en-AU',{minimumFractionDigits:0})}</td>
          </tr>`).join('')}</tbody>
          <tfoot><tr style="background:#dbeafe;font-weight:bold">
            <td colspan="4" style="padding:5px 8px;text-align:right">Section total</td>
            <td style="padding:5px 8px;text-align:right;font-family:monospace">$${total.toLocaleString('en-AU',{minimumFractionDigits:0})}</td>
          </tr></tfoot>
        </table>`
    }).join('')}
    <div style="margin-top:12px;background:#1F3864;color:white;padding:10px 14px;border-radius:6px;display:flex;justify-content:space-between">
      <strong>TENDER TOTAL (ex GST)</strong>
      <strong style="font-family:monospace">$${tender.summary.tenderExGST.toLocaleString('en-AU',{minimumFractionDigits:0})}</strong>
    </div>
  </div>`

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Tender Document — ${tender.reference}</title>
  <style>body{font-family:Arial,sans-serif;font-size:11pt;color:#111;margin:20mm}h1{font-size:22pt;font-weight:900;color:#1F3864;margin:0}
  .hdr{border-bottom:3px solid #1F3864;padding-bottom:12px;margin-bottom:18px;display:flex;justify-content:space-between;align-items:flex-end}
  .ref-box{background:#e8f0fe;border-left:4px solid #1F3864;padding:10px 16px;margin:14px 0;font-size:10pt}
  .footer{font-size:7.5pt;color:#9ca3af;text-align:center;margin-top:20px;border-top:1px solid #e5e7eb;padding-top:8px}
  p{line-height:1.7;margin:0 0 8px}@media print{body{margin:15mm}}</style>
  </head><body>
  <div class="hdr">
    <div><h1>MCB Civil Electrical</h1><div style="font-size:9pt;color:#6b7280">ABN 44 128 993 087 · 14 Commerce Dr, Salisbury QLD 4107 · 07 3344 5500</div></div>
    <div style="text-align:right"><div style="font-size:9pt;color:#6b7280">Date: ${now}</div><div style="font-size:9pt;color:#6b7280">Ref: ${tender.reference}</div></div>
  </div>
  <div class="ref-box"><strong>RFT:</strong> ${tender.rftReference} &nbsp;|  <strong>Client:</strong> ${tender.clientName} &nbsp;|  <strong>Project:</strong> ${tender.projectName}</div>
  <div style="margin:16px 0;background:#1F3864;color:white;padding:14px 20px;border-radius:8px">
    <div style="font-size:8pt;opacity:0.7;text-transform:uppercase;letter-spacing:1px">Full Tender Document — Sections A through J</div>
    <div style="font-size:20pt;font-weight:900;margin:4px 0">${tender.projectName}</div>
    <div style="font-size:11pt;opacity:0.8">Tender Price: $${tender.summary.tenderIncGST.toLocaleString('en-AU',{minimumFractionDigits:0})} inc GST</div>
  </div>
  ${secHtml}
  ${boqHtml}
  <div class="footer">MCB Civil Electrical Pty Ltd · ${tender.reference} · Full Tender Document · Generated ${now} · Confidential</div>
  </body></html>`
  const w = window.open('', '_blank', 'width=1100,height=900')
  if (w) { w.document.write(html); w.document.close(); setTimeout(()=>w.print(), 400) }
}

// ── CM-04: Scope Brief Pipeline Tab ─────────────────────────────────
function PipelineTab() {
  const fmtAUD2 = (n:number) => new Intl.NumberFormat('en-AU',{style:'currency',currency:'AUD',minimumFractionDigits:0}).format(n)
  const fmtDate2= (iso?:string|null) => iso ? new Date(iso).toLocaleDateString('en-AU',{day:'numeric',month:'short'}) : '—'
  const briefs  = MOCK_SCOPE_BRIEFS

  // Funnel KPIs
  const stages = ['RECEIVED','ESTIMATING','QUOTED','SUBMITTED','WON','LOST','NO_QUOTE'] as const
  const counts = Object.fromEntries(stages.map(s => [s, briefs.filter(b=>b.status===s).length]))
  const winRate = briefs.filter(b=>b.status==='WON'||b.status==='LOST').length > 0
    ? Math.round(briefs.filter(b=>b.status==='WON').length / briefs.filter(b=>b.status==='WON'||b.status==='LOST').length * 100)
    : null
  const totalEstimated = briefs.reduce((s,b)=>s+(b.estimatedValue??0),0)

  return (
    <div className="space-y-5">
      {/* Funnel KPIs */}
      <div className="grid grid-cols-7 gap-2">
        {stages.map(stage => {
          const meta  = BRIEF_STATUS_META[stage]
          const count = counts[stage] ?? 0
          return (
            <div key={stage} className="bg-white border border-gray-200 rounded-xl px-3 py-3 text-center">
              <div className={`text-xl font-bold ${meta.text}`}>{count}</div>
              <div className="text-[10px] text-gray-500 font-medium mt-0.5">{meta.label}</div>
            </div>
          )
        })}
      </div>

      {/* Portfolio metrics */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total pipeline value',  value: fmtAUD2(totalEstimated),        color: 'text-[#1F3864]', sub: `${briefs.length} scope briefs in system` },
          { label: 'Active (SUBMITTED+)',    value: briefs.filter(b=>b.status==='SUBMITTED'||b.status==='QUOTED').length, color: 'text-blue-700', sub: 'awaiting client decision' },
          { label: 'Win rate',               value: winRate !== null ? `${winRate}%` : '—', color: winRate && winRate>=50?'text-green-700':'text-amber-700', sub: `${counts.WON} won of ${counts.WON+counts.LOST} decided` },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-xl font-bold ${color}`}>{value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
            <div className="text-xs text-gray-400">{sub}</div>
          </div>
        ))}
      </div>

      {/* Brief register */}
      <div className="space-y-2">
        {briefs.map(brief => {
          const meta     = BRIEF_STATUS_META[brief.status]
          const daysLeft = brief.submissionDeadline
            ? Math.ceil((new Date(brief.submissionDeadline).getTime() - Date.now()) / 86400000)
            : null
          const isUrgent = daysLeft !== null && daysLeft <= 5 && !['WON','LOST','NO_QUOTE'].includes(brief.status)
          return (
            <div key={brief.id} className={`bg-white border rounded-xl p-4 ${
              brief.status==='WON' ? 'border-green-200 bg-green-50/20' :
              brief.status==='LOST' ? 'border-gray-200 opacity-60' :
              isUrgent ? 'border-red-200 bg-red-50/20' : 'border-gray-200'
            }`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-mono text-xs text-gray-400">{brief.briefNumber}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${meta.bg} ${meta.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}/>{meta.label}
                    </span>
                    {isUrgent && daysLeft !== null && (
                      <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-semibold flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5"/>{daysLeft}d to deadline
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-gray-900 mb-1 leading-snug">{brief.title}</p>
                  <div className="flex items-center gap-3 flex-wrap text-xs text-gray-400">
                    <span>{brief.clientName}</span>
                    <span className="font-mono">{fmtAUD2(brief.estimatedValue)}</span>
                    {brief.submissionDeadline && <span>Due: {fmtDate2(brief.submissionDeadline)}</span>}
                    <span>Estimator: {brief.assignedEstimatorName}</span>
                    {brief.linkedJobNumber && <span className="text-green-600 font-medium">→ {brief.linkedJobNumber}</span>}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function Estimating() {
  const { isTier, user } = useAuthStore()
  const isAdmin = isTier('A_DEVELOPER')
  const isPM    = isTier('B_PM')
  const isPC    = user?.tier === 'B_PC'

  const [tab, setTab] = useState<'drawings' | 'estimate' | 'tenders' | 'actuals' | 'pipeline'>('tenders')

  const activeTenderCount  = getActiveTenders().length
  const urgentTenderCount  = getActiveTenders().filter(t => {
    const d = getDaysUntilDeadline(t.submissionDeadline); return d !== null && d <= 3
  }).length
  const portfolioValue = MOCK_TENDERS.reduce((s,t) => s + t.summary.tenderIncGST, 0)
  const awardedValue   = MOCK_TENDERS.filter(t => t.status === 'AWARDED').reduce((s,t) => s + (t.awardedAmount ?? 0), 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileSearch className="w-5 h-5 text-[#2E75B6]" />Estimating & Tender
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Drawings & specs library · BOQ estimate builder · formal tender preparation and submission
          </p>
        </div>
        {urgentTenderCount > 0 && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-2 text-sm text-red-700 font-medium">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            {urgentTenderCount} tender{urgentTenderCount !== 1 ? 's' : ''} closing within 3 days
          </div>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Active tenders',     value: activeTenderCount.toString(), sub: 'in progress', icon: FileText,  color: 'text-[#2E75B6]' },
          { label: 'Portfolio value',    value: fmtAUD(portfolioValue),       sub: 'inc GST',    icon: TrendingUp, color: 'text-[#1F3864]' },
          { label: 'Awarded value',      value: fmtAUD(awardedValue),         sub: 'confirmed',   icon: CheckCircle,color: 'text-green-700' },
          { label: 'Client documents',   value: MOCK_CLIENT_DOCUMENTS.length.toString(), sub: 'in library', icon: Folder, color: 'text-amber-600' },
        ].map(({ label, value, sub, icon: Icon, color }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3.5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className={`text-xl font-bold ${color}`}>{value}</div>
                <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
                <div className="text-xs text-gray-400">{sub}</div>
              </div>
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tab strip */}
      <div className="flex border-b border-gray-200">
        {([
          { key: 'pipeline', label: 'Pipeline',            icon: TrendingUp, count: MOCK_SCOPE_BRIEFS.length },
          { key: 'tenders',  label: 'Tenders',              icon: FileText,  count: MOCK_TENDERS.length },
          { key: 'drawings', label: 'Drawings & Specs',   icon: Folder,    count: MOCK_CLIENT_DOCUMENTS.length },
          { key: 'estimate', label: 'Estimate Builder',   icon: BarChart3, count: null },
          { key: 'actuals',  label: 'Actuals vs Estimate', icon: TrendingUp, count: null },
        ] as const).map(({ key, label, icon: Icon, count }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === key ? 'border-[#2E75B6] text-[#2E75B6]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
            <Icon className="w-4 h-4" />
            {label}
            {count !== null && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${tab === key ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{count}</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'pipeline' && <PipelineTab/>}
      {tab === 'tenders'  && <TendersTab isPM={isPM || isAdmin} isPC={isPC || isPM || isAdmin} />}
      {tab === 'drawings' && <DrawingsSpecsTab canUpload={isPM || isPC || isAdmin} />}
      {tab === 'estimate' && <EstimateBuilderTab canEdit={isPM || isPC || isAdmin} />}
      {tab === 'actuals' && (() => {
        // Get the first awarded tender's BOQ for comparison
        const awardedTenders = getActiveTenders().filter(t => t.status === 'AWARDED')
        if (awardedTenders.length === 0) return (
          <div className="text-center py-16 text-gray-400">
            <TrendingUp className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No awarded tenders to compare actuals against</p>
            <p className="text-xs mt-1">Actuals vs estimate comparison is available once a tender has been awarded and site works have commenced.</p>
          </div>
        )
        const tender = awardedTenders[0]
        const actuals = computeActualsVsEstimate(tender.jobId, tender.reference, tender.boqItems ?? [])
        const hasData = actuals.some(a => a.status !== 'NO_DATA')
        const overItems = actuals.filter(a => a.status === 'OVER')
        const underItems = actuals.filter(a => a.status === 'UNDER')
        const totalVariance = actuals.reduce((s, a) => s + a.varianceDollar, 0)
        return (
          <div className="space-y-4">
            {/* Summary row */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'BOQ labour items', value: actuals.length, sub: 'with SOP reference', warn: false },
                { label: 'Over estimate', value: overItems.length, sub: overItems.map(o => o.description.split(' ').slice(0,3).join(' ')).slice(0,2).join(', ') || 'None', warn: overItems.length > 0 },
                { label: 'Total variance', value: fmtAUD(Math.abs(totalVariance)), sub: totalVariance > 0 ? '▲ over estimate' : totalVariance < 0 ? '▼ under estimate' : 'on track', warn: totalVariance > 5000 },
              ].map(c => (
                <div key={c.label} className={`rounded-xl border p-4 ${c.warn ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
                  <div className="text-xs text-gray-500 mb-1">{c.label}</div>
                  <div className={`text-xl font-bold ${c.warn ? 'text-amber-700' : 'text-gray-900'}`}>{c.value}</div>
                  <div className="text-xs text-gray-400 mt-0.5 truncate">{c.sub}</div>
                </div>
              ))}
            </div>

            {!hasData && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-sm text-indigo-800">
                <p className="font-medium mb-1">No timesheet data yet for this job</p>
                <p className="text-xs text-indigo-600">Once timesheets are submitted and approved for this job's employees, actual hours will appear here and be compared against the BOQ labour estimate.</p>
              </div>
            )}

            {/* Comparison table */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700">Labour actuals vs BOQ estimate — {tender.projectName}</span>
                {isPM && hasData && (
                  <button onClick={() => updateHistoricalRates(tender.jobId, actuals)} className="text-xs text-indigo-600 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 px-2.5 py-1 rounded-lg flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" />Update historical rates from this job
                  </button>
                )}
              </div>
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">BOQ item</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">BOQ estimate</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">Actual cost est.</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">Variance</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Status</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Historical rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {actuals.map(a => {
                    const hist = suggestEstimateRate(a.sopRef)
                    const statusColors = {
                      ON_TRACK: 'bg-green-100 text-green-700',
                      OVER:     'bg-red-100 text-red-700',
                      UNDER:    'bg-blue-100 text-blue-700',
                      NO_DATA:  'bg-gray-100 text-gray-500',
                    }
                    return (
                      <tr key={a.boqItemId} className={a.status === 'OVER' ? 'bg-red-50/30' : a.status === 'UNDER' ? 'bg-blue-50/20' : ''}>
                        <td className="py-2 px-3 max-w-[200px]">
                          <div className="font-medium text-gray-900 truncate">{a.description}</div>
                          <div className="text-gray-400 font-mono mt-0.5">{a.sopRef}</div>
                        </td>
                        <td className="py-2 px-3 text-right">{fmtAUD(a.plannedCostTotal)}</td>
                        <td className="py-2 px-3 text-right">{a.status === 'NO_DATA' ? '—' : fmtAUD(a.actualCostEstimate)}</td>
                        <td className={`py-2 px-3 text-right font-medium ${a.varianceDollar > 0 ? 'text-red-700' : a.varianceDollar < 0 ? 'text-blue-700' : 'text-gray-400'}`}>
                          {a.status === 'NO_DATA' ? '—' : `${a.varianceDollar > 0 ? '+' : ''}${fmtAUD(a.varianceDollar)} (${a.variancePct.toFixed(0)}%)`}
                        </td>
                        <td className="py-2 px-3">
                          <span className={`px-1.5 py-0.5 rounded-full font-medium text-[10px] ${statusColors[a.status]}`}>{a.status.replace('_', ' ')}</span>
                        </td>
                        <td className="py-2 px-3">
                          {hist.confidence === 'NO_DATA'
                            ? <span className="text-gray-400">No data</span>
                            : <><span className={`px-1 py-0.5 rounded text-[10px] font-medium ${hist.confidence === 'HIGH' ? 'bg-green-50 text-green-700' : hist.confidence === 'MEDIUM' ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-500'}`}>{hist.confidence}</span><span className="text-gray-500 ml-1.5">{fmtAUD(hist.suggested ?? 0)}/unit · {hist.sampleCount} job{hist.sampleCount !== 1 ? 's' : ''}</span></>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-xs text-green-800">
              <p className="font-semibold mb-0.5">How historical rates work</p>
              <p>When you click "Update historical rates from this job", the actual hours from timesheets are compared against the BOQ quantities and the resulting cost-per-unit is recorded in the rate library. Future estimates automatically suggest these historical rates when the same SOP is selected — replacing the theoretical hour estimates with real-world performance data from your own crews.</p>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
